import { useState } from "react";
import {
  ALL_SUBJECTS,
  SUBJECT_TITLES,
  teacherSubjects,
  type MeResponse,
  type Subject,
} from "@edu/shared";
import { ApiError, setSubjects } from "../../lib/api.ts";
import { subjectSaveErrorText } from "../../lib/subject-text.ts";
import { Dialog } from "./Dialog.tsx";

/**
 * Мои предметы: какие предметы ведёт преподаватель. Инструменты остальных
 * прячутся: формулы и графики у того, кто не ведёт математику, пропуски в
 * тексте у того, кто не ведёт языки.
 *
 * ⚠️ Отмечен хотя бы один, и это держит кнопка, а не отказ сервера: «ни одного
 * предмета» спрятало бы все инструменты разом. Сервер отказал бы тоже, но
 * `invalid_body` здесь был бы непонятной ошибкой вместо выключенной кнопки.
 *
 * Сохраняется по кнопке, а не на каждую галочку, в отличие от окна учёта:
 * снять последний предмет, чтобы поставить другой, человек вправе, и
 * промежуточное «ни одного» не должно уходить на сервер.
 *
 * Окном по той же причине, что учёт занятий: экрана профиля в продукте нет.
 */
export function SubjectsDialog(props: {
  me: MeResponse;
  onDone: (me: MeResponse) => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<Subject[]>(() => teacherSubjects(props.me.subjects));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /**
   * ⚠️ Порядок держит `ALL_SUBJECTS`, а НЕ `teacherSubjects`: тот читает пустой
   * список как «все», и снятая последняя галочка вернула бы все четыре.
   */
  const toggle = (subject: Subject, on: boolean): void => {
    setChosen((was) => ALL_SUBJECTS.filter((s) => (s === subject ? on : was.includes(s))));
  };

  const save = (): void => {
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        // Ответ заменяет сессию целиком, как у окна учёта
        props.onDone(await setSubjects(chosen));
        props.onClose();
      } catch (e) {
        setErr(subjectSaveErrorText(e instanceof ApiError ? e.code : null));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Dialog
      title="Мои предметы"
      onClose={props.onClose}
      foot={
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || chosen.length === 0}
          onClick={save}
        >
          Сохранить
        </button>
      }
    >
      {err !== null && <p className="err">{err}</p>}

      <div className="form">
        <p className="hint">
          Инструменты предметов без галочки не показываются ни в кабинете, ни на уроке.
        </p>
        {ALL_SUBJECTS.map((subject) => (
          <label key={subject} className="form__check">
            <input
              type="checkbox"
              checked={chosen.includes(subject)}
              disabled={busy}
              onChange={(e) => toggle(subject, e.target.checked)}
            />
            {SUBJECT_TITLES[subject]}
          </label>
        ))}
        {chosen.length === 0 && <p className="hint">Отметьте хотя бы один предмет.</p>}
      </div>
    </Dialog>
  );
}
